import { BeeswaxReportProperties } from '@/Enums/Report/BeeswaxReportProperties.enum';
import { GEO_JOINT_BOUNDARIES, ZIPCODE_ENDPOINT } from '@/constants/endpoints';
import { MAPBOX_GEOCODING_API, MAP_SCALES, US_GEOGRAPHIC_BOUNDS, borderIOHeaders } from '@/constants/geo';
import { circle } from '@turf/turf';
import axios from 'axios';

const ZOOM_LEVELS = [
  { maxRadius: 1, zoom: 12 },
  { maxRadius: 3, zoom: 11 },
  { maxRadius: 7, zoom: 10 },
  { maxRadius: 14, zoom: 9 },
  { maxRadius: 29, zoom: 8 },
  { maxRadius: 55, zoom: 7 },
  { maxRadius: 109, zoom: 6 },
  { maxRadius: 180, zoom: 5 },
  { maxRadius: 400, zoom: 4 },
];

export const getMapZoomBasedOnRadius = radius => {
  const currentLevel = ZOOM_LEVELS.find(level => radius <= level.maxRadius);
  return currentLevel?.zoom ?? 3;
};

const getBoundsFromArea = impressionsArea => {
  const coordinates = impressionsArea.geometry.coordinates;
  let bounds = {
    north: -90,
    south: 90,
    east: -180,
    west: 180,
  };

  coordinates.forEach(polygon => {
    if (Array.isArray(polygon[0][0])) {
      polygon.forEach(subPolygon => {
        subPolygon.forEach(coordinate => {
          const [longitude, latitude] = coordinate;
          bounds.north = Math.max(bounds.north, latitude);
          bounds.south = Math.min(bounds.south, latitude);
          bounds.east = Math.max(bounds.east, longitude);
          bounds.west = Math.min(bounds.west, longitude);
        });
      });
    } else {
      polygon.forEach(coordinate => {
        const [longitude, latitude] = coordinate;
        bounds.north = Math.max(bounds.north, latitude);
        bounds.south = Math.min(bounds.south, latitude);
        bounds.east = Math.max(bounds.east, longitude);
        bounds.west = Math.min(bounds.west, longitude);
      });
    }
  });

  return bounds;
};

export const fitBounds = (geojson, setViewState) => {
  if (!geojson?.features) return;
  const impressionsArea = geojson.features[0];
  const bounds = getBoundsFromArea(impressionsArea);
  const newViewState = {
    latitude: (bounds.north + bounds.south) / 2,
    longitude: (bounds.east + bounds.west) / 2,
    zoom: 8,
    bearing: 0,
    pitch: 0,
  };
  setViewState(newViewState);
};

export const fitCityBounds = (geojson, setViewState) => {
  if (!geojson?.features?.length) return;
  const sortedGeojson = geojson.features.sort((a, b) => b.properties.Impressions - a.properties.Impressions);
  fitBounds({ features: sortedGeojson }, setViewState);
};

export const filterDmaByZipList = (features, zipCodeList) => {
  if (!features) {
    return null;
  }
  return features.filter(feature => {
    return (
      feature.properties &&
      feature.properties.zipCodes &&
      feature.properties.zipCodes.some(zipCode => zipCodeList.includes(zipCode))
    );
  });
};

export const createCircleByRadius = (coordinates, radius) => {
  const circles = circle(coordinates, radius, { units: 'miles' });
  circles.properties.type = 'Circle';

  return circles;
};

export const extractZipCodes = data => {
  const zipCodes = [];
  data.features.forEach(feature => {
    if (feature.properties && feature.properties.zipCode) {
      zipCodes.push(feature.properties.zipCode);
    }
  });
  return zipCodes;
};

export const fetchCities = async geoIds => {
  try {
    const response = await axios.post(GEO_JOINT_BOUNDARIES, {
      data: [
        {
          geoType: MAP_SCALES.CITY,
          geoIds,
        },
      ],
    });
    return response.data?.find(({ type }) => type === MAP_SCALES.CITY)?.featureCollection?.features || [];
  } catch (err) {
    console.error('Failed to fetch city data:', err);
    return [];
  }
};

const MAX_ZIP_BATCH = 100;

export const fetchZips = async (zipcodes = []) => {
  let start = 0;
  const promises = [];
  while (start < zipcodes.length) {
    promises.push(
      axios.get(ZIPCODE_ENDPOINT, {
        params: {
          zipcode: zipcodes.slice(start, start + MAX_ZIP_BATCH),
        },
        headers: borderIOHeaders,
      }),
    );
    start += MAX_ZIP_BATCH;
  }

  const responses = await Promise.all(promises);

  return responses.map(response => response.data.features).flat();
};

const VITE_MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;

export const fetchGeoData = async address => {
  const response = await axios.get(`${MAPBOX_GEOCODING_API}${address}.json`, {
    params: { access_token: VITE_MAPBOX_TOKEN, country: 'us' },
  });
  return response.data;
};

const FEATURE_API_NAME_MAP = {
  [MAP_SCALES.STATE]: p => p.stateCode,
  [MAP_SCALES.DMA]: p => (p.CODE != null ? String(p.CODE) : null),
  [MAP_SCALES.COUNTY]: p => (p.GEOID != null ? String(p.GEOID) : null),
  [MAP_SCALES.CONGRESSIONAL_DISTRICTS]: p => (p.OFFICE_ID != null ? String(p.OFFICE_ID) : null),
  [MAP_SCALES.CITY]: p => {
    const key = p.GEO_ID ?? p.GEOID ?? p.CITY_ID ?? p.GEOID10;
    return key != null ? String(key) : null;
  },
  [MAP_SCALES.COUNTRY]: (_p, feat) => (feat?.id != null ? String(feat.id) : null),
};

const getFeatureApiName = (feat, type) => {
  const p = feat?.properties;
  return FEATURE_API_NAME_MAP[type]?.(p, feat) ?? null;
};

export const getBatchLocationFeatures = async geoLocations => {
  if (!geoLocations.length) return { features: [], notFoundIds: [] };

  const byType = geoLocations.reduce((grouped, location) => {
    (grouped[location.type] ??= []).push(location);
    return grouped;
  }, {});

  try {
    const response = await axios.post(GEO_JOINT_BOUNDARIES, {
      data: Object.entries(byType).map(([geoType, locations]) => ({
        geoType,
        geoIds: locations.map(location => location.apiName),
      })),
    });

    const features = [];
    const notFoundIds = [];

    for (const [type, locations] of Object.entries(byType)) {
      const typeFeatures = response.data?.find(entry => entry.type === type)?.featureCollection?.features || [];

      for (const location of locations) {
        const locationFeatures = typeFeatures
          .filter(feat => feat.geometry?.type && getFeatureApiName(feat, type) === location.apiName)
          .map(feat => ({
            ...feat,
            properties: { ...feat.properties, locationType: type },
            id: location.id,
          }));

        if (locationFeatures.length) {
          features.push(...locationFeatures);
        } else {
          notFoundIds.push(location.id);
        }
      }
    }

    return { features, notFoundIds };
  } catch (err) {
    console.error('Failed to fetch location features:', err);
    return { features: [], notFoundIds: [] };
  }
};
//#endregion

export const extractPointsFromLocations = (locations, radiusCircle) => {
  const points = [];
  const allLocations = [...locations];
  if (radiusCircle) allLocations.push(radiusCircle);

  const addPointsFromGeometry = geometry => {
    if (geometry.type === 'Polygon') {
      points.push(...geometry.coordinates[0]);
    } else if (geometry.type === 'MultiPolygon') {
      geometry.coordinates.forEach(polygon => {
        points.push(...polygon[0]);
      });
    }
  };

  allLocations.forEach(({ geometry }) => {
    if (geometry.geometries?.length) {
      geometry.geometries.forEach(addPointsFromGeometry);
    } else {
      addPointsFromGeometry(geometry);
    }
  });

  return points;
};

export const filterPoints = point => {
  const longitude = point[0];
  const latitude = point[1];

  return (
    (longitude > US_GEOGRAPHIC_BOUNDS.ALASKA.LON_MIN &&
      longitude < US_GEOGRAPHIC_BOUNDS.ALASKA.LON_MAX &&
      latitude > US_GEOGRAPHIC_BOUNDS.ALASKA.LAT_MIN &&
      latitude < US_GEOGRAPHIC_BOUNDS.ALASKA.LAT_MAX) ||
    (longitude > US_GEOGRAPHIC_BOUNDS.MAINLAND.LON_MIN &&
      longitude < US_GEOGRAPHIC_BOUNDS.MAINLAND.LON_MAX &&
      latitude > US_GEOGRAPHIC_BOUNDS.MAINLAND.LAT_MIN &&
      latitude < US_GEOGRAPHIC_BOUNDS.MAINLAND.LAT_MAX)
  );
};

export const getLocationsWithGeometry = (
  locationsToShow,
  locations,
  setLocations,
  setIsLoading = () => {
    /* no-op */
  },
  reportNotFoundLocations = () => {
    /* no-op */
  },
) => {
  const locationsToShowIds = locationsToShow.map(({ id }) => id);
  const locationsIds = [...new Set(locations.map(({ id }) => id))];

  if (locationsToShowIds.length === locationsIds.length && locationsToShowIds.every(id => locationsIds.includes(id))) {
    return;
  }

  setIsLoading(true);

  const addedLocations = locationsToShow.filter(({ id }) => !locations.find(prevLocation => prevLocation.id === id));
  const removedLocationsIds = locations.reduce((acc, { id }) => {
    if (!locationsToShow.find(location => location.id === id)) {
      acc.push(id);
    }
    return acc;
  }, []);

  if (removedLocationsIds.length) {
    setLocations(locations.filter(({ id }) => !removedLocationsIds.includes(id)));
  }

  if (addedLocations.length) {
    (async () => {
      try {
        const { zipsLocations, geoLocations } = addedLocations.reduce(
          (acc, location) => {
            if (location.type === MAP_SCALES.ZIP) {
              acc.zipsLocations.push(location);
            } else {
              acc.geoLocations.push(location);
            }
            return acc;
          },
          { zipsLocations: [], geoLocations: [] },
        );

        const notFoundLocationsIds = [];

        const locationsPromises = [
          ...(geoLocations.length
            ? [
                (async () => {
                  const { features, notFoundIds } = await getBatchLocationFeatures(geoLocations);
                  notFoundIds.forEach(id => notFoundLocationsIds.push(id));
                  return features;
                })(),
              ]
            : []),
          ...(zipsLocations.length
            ? [
                (async () => {
                  const features = await fetchZips(zipsLocations.map(({ apiName }) => apiName));

                  const resultZips = features.map(feat => ({
                    ...feat,

                    // eslint-disable-next-line max-nested-callbacks
                    id: zipsLocations.find(({ apiName }) => apiName === feat.properties.zipCode)?.id,
                  }));

                  zipsLocations.forEach(({ id }) => {
                    // eslint-disable-next-line max-nested-callbacks
                    if (!resultZips.find(zip => zip.id === id)) {
                      notFoundLocationsIds.push(id);
                    }
                  });

                  return resultZips;
                })(),
              ]
            : []),
        ];

        if (!locationsPromises.length) {
          return;
        }

        const fetchedLocations = await Promise.all(locationsPromises);

        if (notFoundLocationsIds.length) {
          reportNotFoundLocations(notFoundLocationsIds);
        }

        setLocations([...locations, ...fetchedLocations.flat(3)]);
        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        console.error('Failed to fetch locations:', err);
      }
    })();
  }
};

export const pickRegionKey = feature => {
  const p = feature?.properties;
  if (!p) return null;

  const lt = (p.locationType || p.type || '').toString().toLowerCase();

  // ZIP
  if (p.zipCode || lt === 'zip') return { type: 'ZIP', key: String(p.zipCode) };

  // State
  if ((lt === 'state' || p.locationType === 'State' || p.locationType === 'STATE') && p.stateCode)
    return { type: 'State', key: String(p.stateCode) };

  // DMA
  if (lt === 'dma' && p.CODE) return { type: 'DMA', key: String(p.CODE) };
  if (!lt && p.CODE) return { type: 'DMA', key: String(p.CODE) };

  // Congressional Districts
  if ((lt === 'congressional districts' || p.locationType === 'Congressional Districts') && p.OFFICE_ID)
    return { type: 'Congressional Districts', key: String(p.OFFICE_ID) };

  // City
  if (lt === 'city' || p.locationType === 'City') {
    const cityKey = p.GEO_ID ?? p.GEOID ?? p.CITY_ID ?? p.GEOID10;
    if (cityKey != null) return { type: 'City', key: String(cityKey) };
  }

  // County
  if (lt === 'county' && p.GEOID) return { type: 'County', key: String(p.GEOID) };

  // Fallbacks if location type is missing
  if (p.OFFICE_ID) return { type: 'Congressional Districts', key: String(p.OFFICE_ID) };
  if (p.GEO_ID) return { type: 'City', key: String(p.GEO_ID) };
  if (p.GEOID) return { type: 'County', key: String(p.GEOID) };

  return null;
};

export const mergeGeoData = (feature, impressionsIndex) => {
  if (!feature) return null;

  const match = pickRegionKey(feature);
  if (!match) return null;

  const { type, key } = match;
  const byType = impressionsIndex?.get(type);
  const impressions = byType?.get(String(key));
  if (!impressions) return null;

  const p = feature.properties || {};
  const extraTitleProps = (() => {
    if ((p.locationType === 'State' || p.locationType === 'STATE') && p.stateCode) {
      return { [BeeswaxReportProperties.GEO_STATE]: p.name || p.stateCode };
    }
    if (p.zipCode) {
      return { [BeeswaxReportProperties.GEO_ZIP]: p.zipCode };
    }
    if (p.CODE) {
      return {
        [BeeswaxReportProperties.DMA_NAME]: p.NAME || p.name,
        [BeeswaxReportProperties.DMA_CODE]: String(p.CODE),
      };
    }
    if (p.GEOID) {
      return {
        [BeeswaxReportProperties.GEO_COUNTY]: p.NAME || p.name,
        [BeeswaxReportProperties.GEO_COUNTY_ID]: String(p.GEOID),
      };
    }
    if (p.OFFICE_ID) {
      const officeId = String(p.OFFICE_ID);
      const state = officeId.slice(0, 2);
      const district = String(parseInt(officeId.slice(2), 10));
      return { [BeeswaxReportProperties.GEO_CONGRESSIONAL_DISTRICTS]: `${state}-${district}` };
    }
    if (
      p.locationType === 'City' ||
      (p.locationType || '').toString().toLowerCase() === 'city' ||
      p.GEO_ID ||
      p.GEOID ||
      p.CITY_ID ||
      p.GEOID10
    ) {
      const cityKey = p.GEO_ID ?? p.GEOID ?? p.CITY_ID ?? p.GEOID10;
      if (cityKey != null) {
        return {
          [BeeswaxReportProperties.CITY]: p.NAME || p.name,
          [BeeswaxReportProperties.CITY_CODE]: String(cityKey),
        };
      }
    }
    return {};
  })();

  return {
    ...feature,
    properties: {
      ...feature.properties,
      Impressions: impressions,
      ...extraTitleProps,
    },
  };
};
